/**
 * Shared form utilities for M3 application
 * Contains reusable functions for form validation, formatting, and UI interactions
 */

/**
 * Custom action to ensure proper scrolling behavior in forms on all devices
 * This is particularly important for iOS where scroll momentum and bounds 
 * can create usability issues.
 * 
 * @param {HTMLElement} node - The scrollable container element
 * @returns {object} - Svelte action lifecycle methods
 */
export function enableFormScrolling(node) {
  // Ensure proper scrolling on iOS devices
  function handleTouchStart(e) {
    // Allow default touch behavior for scrolling
    const scrollTop = node.scrollTop;
    const scrollHeight = node.scrollHeight;
    const height = node.clientHeight;
    const startY = e.touches[0].clientY;
    
    // Store touch start position
    node.dataset.touchStartY = startY;
    node.dataset.scrollTop = scrollTop;
    
    // Check if at top or bottom of scroll container
    if ((scrollTop <= 0 && e.touches[0].clientY > 0) || 
        (scrollTop + height >= scrollHeight && e.touches[0].clientY < 0)) {
      // At boundary, prevent default to avoid body scroll
      e.preventDefault();
    }
  }
  
  // Keep scroll momentum contained within this element
  function handleTouchMove(e) {
    const startY = Number(node.dataset.touchStartY || 0);
    const currentY = e.touches[0].clientY;
    const scrollTop = Number(node.dataset.scrollTop || 0);
    const scrollHeight = node.scrollHeight;
    const height = node.clientHeight;
    
    // If already at top and trying to scroll down or at bottom and trying to scroll up
    if ((scrollTop <= 0 && currentY > startY) || 
        (scrollTop + height >= scrollHeight && currentY < startY)) {
      // Prevent page overscroll
      e.preventDefault();
    }
  }
  
  node.addEventListener('touchstart', handleTouchStart, { passive: false });
  node.addEventListener('touchmove', handleTouchMove, { passive: false });
  
  return {
    destroy() {
      node.removeEventListener('touchstart', handleTouchStart);
      node.removeEventListener('touchmove', handleTouchMove);
    }
  };
}

/**
 * Process validation errors from the API response into a user-friendly format
 * Maps server validation errors to specific form fields
 * 
 * @param {object} errorResponse - The error response from the API
 * @param {object} fieldErrors - The current field errors object to update
 * @returns {string} - A general error message for display
 */
export function processFormValidationErrors(errorResponse, fieldErrors = {}) {
  // Reset all field errors if provided
  if (fieldErrors) {
    Object.keys(fieldErrors).forEach(key => {
      fieldErrors[key] = '';
    });
  }
  
  // Error response might be directly in err.response or nested further
  const validationErrors = errorResponse?.response || errorResponse;
  
  if (!validationErrors || typeof validationErrors !== 'object') {
    return errorResponse?.message || 'Failed to save data';
  }
  
  // Handle field-specific errors if fieldErrors object is provided
  if (fieldErrors) {
    // Map server error fields to local fields
    Object.entries(validationErrors).forEach(([key, value]) => {
      if (key in fieldErrors) {
        fieldErrors[key] = Array.isArray(value) ? value[0] : value;
      }
    });
  }
  
  // Check for non-field errors
  if (validationErrors.non_field_errors || validationErrors.detail) {
    return Array.isArray(validationErrors.non_field_errors) 
      ? validationErrors.non_field_errors[0] 
      : (validationErrors.detail || 'Validation failed');
  }
  
  // Default message if we found field errors
  if (fieldErrors && Object.values(fieldErrors).some(error => error)) {
    return 'Please correct the highlighted fields';
  }
  
  // Fallback error message
  return errorResponse?.message || 'Failed to save data';
}

/**
 * Format a phone number for display in the UI
 * 
 * @param {string} input - Raw phone number input
 * @returns {string} - Formatted phone number for display
 */
export function formatDisplayPhone(input) {
  if (!input) return '';
  const numbers = input.replace(/\D/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
}

/**
 * Format a phone number for API submission
 * 
 * @param {string} phone - Display-formatted phone number
 * @returns {string|null} - API-formatted phone number or null if invalid
 */
export function formatApiPhone(phone) {
  if (!phone) return null;
  const numbers = phone.replace(/\D/g, '');
  // Allow both 10-digit numbers and accept if already formatted with +1
  if (numbers.length === 10) {
    return `+1${numbers}`; // Format properly for API with country code
  } else if (numbers.length === 11 && numbers.startsWith('1')) {
    return `+${numbers}`; // Already has country code, just add +
  }
  return null; // Invalid phone number format
}

/**
 * Set body classes to prevent background scrolling when a modal is open
 * 
 * @param {boolean} isOpen - Whether the modal is open or closed
 */
export function setBodyScrollLock(isOpen) {
  if (typeof document === 'undefined') return;
  
  if (isOpen) {
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
  } else {
    document.body.style.overflow = '';
    document.body.classList.remove('modal-open');
  }
}

/**
 * Add global styles required for form utilities
 * This should be imported once in the main app component
 */
export function addFormStyles() {
  if (typeof document === 'undefined') return;
  
  // Create a style element if it doesn't exist
  let styleEl = document.getElementById('m3-form-utils-styles');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'm3-form-utils-styles';
    document.head.appendChild(styleEl);
  }
  
  // Add global styles needed for forms
  styleEl.innerHTML = `
    /* Prevent body scrolling while modal is open */
    body.modal-open {
      overflow: hidden !important;
      position: fixed;
      width: 100%;
      height: 100%;
    }
    
    /* Ensure error messages are visible */
    .error-text {
      color: #dc2626;
      font-size: 0.8rem;
      margin-top: 0.25rem;
      display: block;
    }
    
    /* Standard form error icon positioning */
    .input-wrapper {
      position: relative;
      width: 100%;
    }
    
    .error-icon {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: #dc2626;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }
    
    /* Enhanced scrollable container */
    .scrollable-form-container {
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      scroll-behavior: smooth;
      overscroll-behavior: contain;
      position: relative;
      z-index: 1;
    }
  `;
}